












































































































































































































































































































import { Component, Vue } from "vue-property-decorator";
import { ILocalePair, IRelatedLink } from "@/@types/servers/methods-id";

import { auth } from "@/config/host.config";
import { normalizeMethodsTree } from "@/services/admin/methods-tree";
import StandartModal from "@/components/app/standart-modal.vue";

interface IRouteNode {
  route: string;
  name: ILocalePair;
  description: ILocalePair;
  related: IRelatedLink[];
}

interface IServerNode {
  server: string;
  port: number;
  name: ILocalePair;
  show: boolean;
  routes: IRouteNode[];
}

@Component({
  components: { StandartModal },
})
class MethodsIdEditor extends Vue {
  tree: IServerNode[] = [];
  loading = true;
  loadError = false;
  loadStatus = 0;
  errMessage = "";
  busy = false;
  search = "";
  selected = "";

  openServers: { [key: string]: boolean } = {};

  newRef = "";
  newKind = "dictionary";
  newScope = "read";
  newRequired = false;

  kinds = ["dictionary", "images", "system"];
  scopes = ["read", "write", "all"];

  name(field: ILocalePair): string {
    return this.$getLocale(field);
  }

  get creatorRole(): string {
    return String(this.$store.getters.getUserRole || "");
  }

  get kindOptions(): Array<{ value: string; text: string }> {
    return this.kinds.map((k) => ({
      value: k,
      text: this.$t(`Settings.methods-id.kind.${k}`) as string,
    }));
  }

  get scopeOptions(): Array<{ value: string; text: string }> {
    return this.scopes.map((s) => ({
      value: s,
      text: this.$t(`Settings.methods-id.scope.${s}`) as string,
    }));
  }

  get refOptions(): Array<{ value: string; text: string }> {
    const out: Array<{ value: string; text: string }> = [];
    for (const srv of this.tree) {
      for (const route of srv.routes) {
        const ref = `${srv.server}/${route.route}`;
        if (ref == this.selected) continue;
        out.push({
          value: ref,
          text: `${this.name(route.name)} (${ref})`,
        });
      }
    }
    return out;
  }

  isOpen(key: string): boolean {
    return !!this.openServers[key];
  }

  toggle(key: string): void {
    this.$set(this.openServers, key, !this.openServers[key]);
  }

  visibleRoutes(srv: IServerNode): IRouteNode[] {
    const q = this.search.trim().toLowerCase();
    if (!q) return srv.routes;
    return srv.routes.filter(
      (r) =>
        this.name(r.name).toLowerCase().includes(q) ||
        `${srv.server}/${r.route}`.toLowerCase().includes(q)
    );
  }

  relatedCount(srv: IServerNode): number {
    return srv.routes.reduce((sum, r) => sum + (r.related || []).length, 0);
  }

  get selectedRoute(): IRouteNode | null {
    if (!this.selected) return null;
    const [serverKey, routeKey] = this.selected.split("/");
    const srv = this.tree.find((s) => s.server == serverKey);
    if (!srv) return null;
    return srv.routes.find((r) => r.route == routeKey) || null;
  }

  get selectedRelated(): IRelatedLink[] {
    return this.selectedRoute ? this.selectedRoute.related || [] : [];
  }

  refTitle(ref: string): string {
    const [serverKey, routeKey] = ref.split("/");
    const srv = this.tree.find((s) => s.server == serverKey);
    const route = srv ? srv.routes.find((r) => r.route == routeKey) : null;
    return route ? this.name(route.name) : ref;
  }

  select(serverKey: string, routeKey: string): void {
    this.selected = `${serverKey}/${routeKey}`;
    this.newRef = "";
  }

  onError(error: unknown): void {
    const code =
      (error as { response?: { data?: { error?: string } } })?.response?.data
        ?.error || "";
    const text = code
      ? String(this.$i18n.t(`Settings.methods-id.errors.${code}`))
      : String(this.$i18n.t("alerts.emptyUpdate"));
    this.$notify({ group: "global", text, type: "warn" });
  }

  onSuccess(): void {
    this.$notify({
      group: "global",
      text: String(this.$i18n.t("error.200")),
    });
  }

  async loadTree(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    this.loadStatus = 0;
    this.errMessage = "";
    try {
      // /api/methods отдаёт тот же объект api (related + show) и доступен всегда
      const res = await this.$axios.get(auth.getAPI);
      if (res.status == 200) {
        this.tree = this.toServerNodes(res.data);
        if (this.tree.length)
          this.$set(this.openServers, this.tree[0].server, true);
      } else {
        this.loadError = true;
        this.loadStatus = res.status;
      }
    } catch (error) {
      console.log(error);
      this.loadError = true;
      const err = error as {
        response?: { status?: number };
        message?: string;
      };
      this.loadStatus = Number(err?.response?.status || 0);
      this.errMessage = String(err?.message || error);
      this.onError(error);
    } finally {
      this.loading = false;
    }
  }

  /** Methods_ID api (объект или массив) → плоский список сервисов для редактора */
  toServerNodes(api: unknown): IServerNode[] {
    return normalizeMethodsTree(api).map((srv) => ({
      server: srv.key,
      port: srv.port,
      name: srv.name,
      show: srv.show,
      routes: srv.modules.map((mod) => ({
        route: mod.key,
        name: mod.name,
        description: { ru: "", en: "" },
        related: mod.related,
      })),
    }));
  }

  async setVisibility(srv: IServerNode, show: boolean): Promise<void> {
    this.busy = true;
    try {
      const res = await this.$axios.put(`${auth.permissionsLinks}/visibility`, {
        server: srv.server,
        show,
      });
      if (res.status == 200) {
        srv.show = res.data.show;
        this.onSuccess();
      }
    } catch (error) {
      console.log(error);
      this.onError(error);
    } finally {
      this.busy = false;
    }
  }

  async addLink(): Promise<void> {
    if (!this.selected || !this.newRef) return;
    this.busy = true;
    const payload = {
      route: this.selected,
      ref: this.newRef,
      kind: this.newKind,
      scope: this.newScope,
      required: this.newRequired,
    };
    try {
      const res = await this.$axios.post(auth.permissionsLinks, payload);
      if (res.status == 200) {
        this.onSuccess();
        this.newRef = "";
        this.newRequired = false;
        await this.loadTree();
      }
    } catch (error) {
      console.log(error);
      this.onError(error);
    } finally {
      this.busy = false;
    }
  }

  async updateLink(
    link: IRelatedLink,
    field: "kind" | "scope" | "required",
    value: string | boolean
  ): Promise<void> {
    if (!this.selected) return;
    this.busy = true;
    const payload: Record<string, unknown> = {
      route: this.selected,
      ref: link.ref,
    };
    payload[field] = field == "required" ? !!value : String(value);
    try {
      const res = await this.$axios.put(auth.permissionsLinks, payload);
      if (res.status == 200) {
        this.onSuccess();
        await this.loadTree();
      }
    } catch (error) {
      console.log(error);
      this.onError(error);
    } finally {
      this.busy = false;
    }
  }

  async deleteLink(link: IRelatedLink): Promise<void> {
    if (!this.selected) return;
    this.busy = true;
    try {
      const res = await this.$axios.delete(auth.permissionsLinks, {
        data: { route: this.selected, ref: link.ref },
      });
      if (res.status == 200) {
        this.onSuccess();
        await this.loadTree();
      }
    } catch (error) {
      console.log(error);
      this.onError(error);
    } finally {
      this.busy = false;
    }
  }

  mounted(): void {
    this.loadTree();
  }

  destroyed(): void {
    this.tree = [];
  }
}

export default MethodsIdEditor;
